import {
  Component,
  Host,
  h,
  Element,
  Prop,
  Watch,
  State,
  Event,
  EventEmitter,
  Method,
} from '@stencil/core';
//@ts-ignore
import { debounce } from 'lodash';
import { Flavor } from './types';

export interface AhaPaginate extends HTMLAhaPaginateElement {}

/**
 * A simple component to auto-paginate a group of items.
 *
 * Automatically creates pagination controls.
 *
 * @slot default - Contents of the default slot. This should be all the items that you would like to paginate.
 */
@Component({
  tag: 'aha-paginate',
  styleUrl: 'aha-paginate.css',
  shadow: true,
})
export class AhaPaginate {
  @Element() host!: HTMLAhaPaginateElement;

  /** The current page number */
  @Prop({ reflect: true }) page: number = 1;

  /** Number of pages in the the <aha-paginate> component. */
  @State() pages: number = 1;

  /** The style of paginator control. */
  @Prop({ reflect: true }) flavor: Flavor;

  /** Which side the paginator controls should be aligned to. */
  @Prop({ reflect: true }) align: 'left' | 'right' | 'center' = 'center';

  @Watch('page')
  private keepPageInBounds() {
    requestAnimationFrame(() => {
      if (this.page < 1) {
        this.page = 1;
      } else if (this.page > this.pages) {
        this.page = this.pages;
      }
      this.pageChangedEvent.emit(this.host);
    });
  }

  @Watch('page')
  private pageItems() {
    this.paginateItems();
  }

  /** Fires when a page has been changed. */
  @Event({
    eventName: 'aha-paginate:change',
    composed: true,
    cancelable: true,
    bubbles: true,
  }) //eslint-disable-line @stencil/decorators-style
  pageChangedEvent!: EventEmitter<HTMLAhaPaginateElement>;

  private slottedElems: Array<any> = [];

  // A weak map that keeps track of the page on which a node is present.
  private pagedElems = new WeakMap();

  /** A method used to recalculate the number of pages, as well as the items on each page. */
  @Method()
  async buildPages() {
    // Wrap in RAF, otherwise Firefox will sometimes miscalculate the heights of the slotted elements.
    requestAnimationFrame(() => {
      const originalPages = this.pages;
      let pages = 0;
      const box = this.host.shadowRoot?.getElementById('items')?.getBoundingClientRect() || {
        top: 0,
        height: 0,
      };
      const { height: pageHeight } = box;
      let page = 1;
      let currentHeight = 0;
      let prevBottom = 0;

      if (pageHeight) {
        this.slottedElems.forEach((node, i) => {
          node.setAttribute('aria-hidden', 'false');
          let { height, top, bottom } = node.getBoundingClientRect();

          // If `prevBottom` is zero, then the prevBottom and the top are the same value.
          if (prevBottom === 0) prevBottom = top;

          const offset = top - prevBottom;
          prevBottom = bottom;
          currentHeight += height + offset;
          if (currentHeight > pageHeight) {
            // If we start a new page, then set `prevBottom` to 0 so the offset is calculated relative
            // to the `top` of the current page.
            prevBottom = 0;
            page += 1;
            currentHeight = height;
          }
          this.pagedElems.set(node, page);
          if (page > pages) pages = page;
        });
      }
      this.pages = pages;

      // If the current page is outside of the possible page range, then adjust the current page so it is
      // relative to the new possible page range.
      if (this.page > this.pages) this.page = (this.pages / originalPages) * this.page;

      this.paginateItems();
    });
  }

  private paginateItems() {
    let firstShown = false;
    let firstItemInPage = undefined as HTMLElement | undefined;
    let lastItemInPage = undefined as HTMLElement | undefined;
    const elems = this.slottedElems;
    elems.forEach((node, i) => {
      const nodePage = this.pagedElems.get(node);
      node.setAttribute('aria-hidden', this.page === nodePage ? 'false' : 'true');
      delete node.dataset.aha;

      if (this.page === nodePage) {
        if (!firstShown) {
          firstShown = true;

          // Tag this node as the first node in the "page".
          firstItemInPage = node;
        }
        // Tag any node which is visible as the last node in the "page". Because this value will be overriden with each new
        // visible item, the final value actually will be the last visible item.
        lastItemInPage = node;
      } else {
        delete node.dataset.ahaPaginateFirstShown;
        delete node.dataset.ahaPaginateLastShown;
      }
    });

    // Set the dataset value at the end, after all elements have been looped through. This prevents CSS rules (like margin-top: auto)
    // from affecting layout prior the the "pages" being fully established.
    if (firstItemInPage) firstItemInPage.dataset.ahaPaginateFirstShown = '';
    if (lastItemInPage) lastItemInPage.dataset.ahaPaginateLastShown = '';
  }

  private handleSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    this.slottedElems = slot.assignedElements();

    // Switch to the first page, otherwise the height of all pages will be based on the current page, which may only have 1
    // or 2 items if it is the last page.
    this.page = 1;

    this.buildPages();
  }

  private goToStart() {
    this.page = 1;
  }

  private goToPrevPage() {
    if (this.page > 1) this.page -= 1;
  }

  private goToNextPage() {
    if (this.pages && this.page < this.pages) this.page += 1;
  }

  private goToEnd() {
    this.page = this.pages;
  }

  private setPageNumber(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = Number(target.value);
    if (value && !Number.isNaN(value) && value >= 1 && value <= this.pages) {
      this.page = value;
    } else if (typeof value === 'undefined') {
      target.value = String(this.page);
    }
  }

  private handleBlur(e: Event) {
    const target = e.target as HTMLInputElement;
    if (!target.value) target.value = String(this.page);
  }

  connectedCallback() {
    this.handleSlotChange = this.handleSlotChange.bind(this);
    this.goToStart = this.goToStart.bind(this);
    this.goToPrevPage = this.goToPrevPage.bind(this);
    this.goToNextPage = this.goToNextPage.bind(this);
    this.goToEnd = this.goToEnd.bind(this);
    this.setPageNumber = this.setPageNumber.bind(this);
    this.handleBlur = this.handleBlur.bind(this);

    // @ts-ignore
    window.pagedElems = this.pagedElems;
  }

  private itemsWrapper?: HTMLElement;

  componentDidLoad() {
    // Watch for resize events and recalculate the pages.
    const debouncedBuildPages = debounce(() => this.buildPages(), 80);
    if (this.itemsWrapper) {
      new ResizeObserver(entries => {
        entries.forEach(entry => {
          debouncedBuildPages();
        });
      }).observe(this.itemsWrapper);
    }
  }

  render() {
    // We want to leave enough space here so we can change pages without affecting the width of the `pageNumber` div.
    // To do that, we take length of the number of pages (ex. 88 pages is 2 characters), multiply by 2 (because there is the current
    // page number and the total number of pages), and then add 4 for the ` of ` text.
    const widthOfInputField = `${this.pages.toString().length * 2 + 4}ch`;

    return (
      <Host role="region" aria-label="Pagination">
        <div id="items" part="items" ref={elem => (this.itemsWrapper = elem)}>
          <slot onSlotchange={this.handleSlotChange}></slot>
        </div>
        <footer id="paginator-wrapper" aria-hidden={this.pages === 1 ? 'true' : 'false'}>
          <nav id="paginator" part="paginator" role="navigation" aria-label="Pagination navigation">
            <button
              type="button"
              onClick={this.goToStart}
              disabled={this.page === 1}
              aria-disabled={this.page === 1}
              aria-label="Go to first page"
            >
              <aha-icon icon="fa-solid fa-angles-left"></aha-icon>
            </button>
            <button
              type="button"
              onClick={this.goToPrevPage}
              disabled={this.page === 1}
              aria-disabled={this.page === 1}
              aria-label="Go to previous page"
            >
              <aha-icon icon="fa-solid fa-angle-left"></aha-icon>
            </button>
            <div id="pageNumber" style={{ width: widthOfInputField }}>
              <input
                type="number"
                onInput={this.setPageNumber}
                onBlur={this.handleBlur}
                value={this.page}
                min={1}
                max={this.pages}
                aria-label="Enter page number"
              />
              <span aria-label={`Current page is ${this.page}`}>
                {this.page} of {this.pages}
              </span>
            </div>
            <button
              type="button"
              onClick={this.goToNextPage}
              disabled={this.page === this.pages}
              aria-disabled={this.page === this.pages}
              aria-label="Go to next page"
            >
              <aha-icon icon="fa-solid fa-angle-right"></aha-icon>
            </button>
            <button
              type="button"
              onClick={this.goToEnd}
              disabled={this.page === this.pages}
              aria-disabled={this.page === this.pages}
              aria-label="Go to last page"
            >
              <aha-icon icon="fa-solid fa-angles-right"></aha-icon>
            </button>
          </nav>
        </footer>
      </Host>
    );
  }
}
